import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  BookOpen, MapPin, Clock, Calendar, Users, Phone, Mail,
  AlertCircle, CheckCircle2, FileText, GraduationCap, Building2,
  Coffee, Wifi, Car, Train, ArrowRight
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cfa-portal/artifacts/waysuoc0_D35F1E2C-9684-4E11-BB45-5677C422C15C.png";

export const LivretAccueil = () => {
  return (
    <div data-testid="livret-accueil-page">
      {/* Hero */}
      <section className="bg-[#0B1F3A] py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-12 text-center">
          <img src={LOGO_URL} alt="Institut BIA" className="h-20 mx-auto mb-6 brightness-0 invert" />
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
            Livret d'accueil
          </span>
          <h1 className="font-serif-heading text-4xl sm:text-5xl font-semibold text-white mb-4">
            Bienvenue à l'Institut BIA
          </h1>
          <p className="text-[#F5F5F5]/80 text-lg max-w-2xl mx-auto">
            Toutes les informations pratiques pour bien démarrer votre formation 
            TP Négociateur Technico-Commercial
          </p>
        </div>
      </section>

      {/* Sommaire */}
      <section className="py-8 bg-[#C8A75A]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 text-sm">
            <a href="#presentation" className="text-[#0B1F3A] font-medium hover:underline">Présentation</a>
            <a href="#organisation" className="text-[#0B1F3A] font-medium hover:underline">Organisation</a>
            <a href="#reglement" className="text-[#0B1F3A] font-medium hover:underline">Règlement</a>
            <a href="#pratique" className="text-[#0B1F3A] font-medium hover:underline">Infos pratiques</a>
            <a href="#contacts" className="text-[#0B1F3A] font-medium hover:underline">Contacts</a>
          </div>
        </div>
      </section>

      {/* Présentation */}
      <section id="presentation" className="py-16 bg-white scroll-mt-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <span className="text-[#C8A75A] uppercase tracking-[0.2em] text-xs font-semibold mb-4 block">
                Présentation
              </span>
              <h2 className="font-serif-heading text-3xl font-semibold text-[#0B1F3A] mb-6">
                L'Institut BIA
              </h2>
              <div className="w-16 h-0.5 bg-[#C8A75A] mb-6"></div>
              
              <p className="text-[#4A4A4A] mb-4 leading-relaxed">
                L'Institut BIA est un Centre de Formation d'Apprentis (CFA) situé à Saint-Denis (93), 
                dédié à l'excellence commerciale. Notre mission : former les talents commerciaux de demain 
                à travers une pédagogie innovante et un accompagnement personnalisé.
              </p>
              <p className="text-[#4A4A4A] mb-6 leading-relaxed">
                Notre devise <strong className="text-[#C8A75A]">"Bâtir • Innover • Accomplir"</strong> reflète 
                notre engagement à construire avec vous un parcours de réussite.
              </p>

              <div className="bg-[#F5F5F5] p-6 border-l-4 border-[#C8A75A]">
                <h3 className="font-semibold text-[#0B1F3A] mb-2">Notre formation</h3>
                <p className="text-sm text-[#4A4A4A]">
                  TP Négociateur Technico-Commercial (RNCP39063) – Niveau 5 (Bac+2)<br />
                  490 heures en centre • 12 mois de formation
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-[#0B1F3A] p-6 text-white">
                <GraduationCap className="text-[#C8A75A] mb-4" size={32} />
                <h3 className="font-semibold mb-2">Vos avantages apprenti</h3>
                <ul className="space-y-2 text-sm text-[#F5F5F5]/90">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-[#C8A75A] flex-shrink-0 mt-0.5" size={16} />
                    Formation 100% gratuite (financée par OPCO)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-[#C8A75A] flex-shrink-0 mt-0.5" size={16} />
                    Rémunération entre 775€ et 1 867€/mois selon l'âge
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-[#C8A75A] flex-shrink-0 mt-0.5" size={16} />
                    Carte étudiant des métiers
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-[#C8A75A] flex-shrink-0 mt-0.5" size={16} />
                    5 semaines de congés payés
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-[#C8A75A] flex-shrink-0 mt-0.5" size={16} />
                    Diplôme reconnu par l'État
                  </li>
                </ul>
              </div>

              <div className="border border-[#E6E6E6] p-6">
                <Building2 className="text-[#C8A75A] mb-4" size={32} />
                <h3 className="font-semibold text-[#0B1F3A] mb-2">Rythme d'alternance</h3>
                <p className="text-[#4A4A4A] text-sm mb-4">
                  Le rythme le plus productif du marché pour une immersion optimale.
                </p>
                <div className="flex items-center gap-4">
                  <div className="bg-[#0B1F3A] text-white px-4 py-2 text-center">
                    <div className="text-2xl font-bold text-[#C8A75A]">1</div>
                    <div className="text-xs">jour CFA</div>
                  </div>
                  <span className="text-[#C8A75A] font-bold">/</span>
                  <div className="bg-[#C8A75A] text-[#0B1F3A] px-4 py-2 text-center">
                    <div className="text-2xl font-bold">4</div>
                    <div className="text-xs">jours entreprise</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Organisation */}
      <section id="organisation" className="py-16 bg-[#F5F5F5] scroll-mt-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <span className="text-[#C8A75A] uppercase tracking-[0.2em] text-xs font-semibold mb-4 block text-center">
            Organisation
          </span>
          <h2 className="font-serif-heading text-3xl font-semibold text-[#0B1F3A] mb-12 text-center">
            Votre semaine type
          </h2>

          <div className="grid md:grid-cols-5 gap-4 mb-12">
            {[
              { day: 'Lundi', type: 'CFA', color: 'bg-[#0B1F3A] text-white' },
              { day: 'Mardi', type: 'Entreprise', color: 'bg-[#C8A75A] text-[#0B1F3A]' },
              { day: 'Mercredi', type: 'Entreprise', color: 'bg-[#C8A75A] text-[#0B1F3A]' },
              { day: 'Jeudi', type: 'Entreprise', color: 'bg-[#C8A75A] text-[#0B1F3A]' },
              { day: 'Vendredi', type: 'Entreprise', color: 'bg-[#C8A75A] text-[#0B1F3A]' }
            ].map((item, i) => (
              <div key={i} className={`${item.color} p-6 text-center`}>
                <div className="font-semibold">{item.day}</div>
                <div className="text-sm opacity-80">{item.type}</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 border border-[#E6E6E6]">
              <Clock className="text-[#C8A75A] mb-4" size={28} />
              <h3 className="font-semibold text-[#0B1F3A] mb-4">Horaires au CFA (Lundi)</h3>
              <div className="space-y-2 text-sm text-[#4A4A4A]">
                <div className="flex justify-between py-2 border-b border-[#E6E6E6]">
                  <span>Matin</span>
                  <span className="font-medium">9h00 - 12h30</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[#E6E6E6]">
                  <span>Pause déjeuner</span>
                  <span className="font-medium">12h30 - 13h30</span>
                </div>
                <div className="flex justify-between py-2">
                  <span>Après-midi</span>
                  <span className="font-medium">13h30 - 17h00</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 border border-[#E6E6E6]">
              <Calendar className="text-[#C8A75A] mb-4" size={28} />
              <h3 className="font-semibold text-[#0B1F3A] mb-4">Calendrier annuel</h3>
              <ul className="space-y-2 text-sm text-[#4A4A4A]">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="text-[#C8A75A]" size={16} />
                  49 lundis de formation au CFA
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="text-[#C8A75A]" size={16} />
                  ~170 jours en entreprise
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="text-[#C8A75A]" size={16} />
                  Vacances scolaires Zone B
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="text-[#C8A75A]" size={16} />
                  5 semaines de congés payés
                </li>
              </ul>
              <Link to="/planning" className="inline-flex items-center gap-2 text-[#C8A75A] hover:text-[#0B1F3A] text-sm mt-4 font-medium">
                Voir le planning détaillé <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Règlement */}
      <section id="reglement" className="py-16 bg-white scroll-mt-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <span className="text-[#C8A75A] uppercase tracking-[0.2em] text-xs font-semibold mb-4 block text-center">
            Règlement
          </span>
          <h2 className="font-serif-heading text-3xl font-semibold text-[#0B1F3A] mb-12 text-center">
            Vos engagements
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="border border-[#E6E6E6] p-6">
              <h3 className="font-semibold text-[#0B1F3A] mb-4 flex items-center gap-2">
                <CheckCircle2 className="text-green-600" size={20} />
                Ce que vous devez faire
              </h3>
              <ul className="space-y-3 text-sm text-[#4A4A4A]">
                <li>• Être présent et ponctuel chaque lundi au CFA</li>
                <li>• Signer la feuille d'émargement (matin et après-midi)</li>
                <li>• Justifier toute absence sous 48h</li>
                <li>• Remplir le livret de suivi chaque mois</li>
                <li>• Participer aux réunions triadiques</li>
                <li>• Respecter le matériel et les locaux</li>
                <li>• Adopter une tenue professionnelle</li>
              </ul>
            </div>

            <div className="border border-red-200 bg-red-50 p-6">
              <h3 className="font-semibold text-red-700 mb-4 flex items-center gap-2">
                <AlertCircle className="text-red-600" size={20} />
                Ce qui est interdit
              </h3>
              <ul className="space-y-3 text-sm text-red-700">
                <li>• Absences non justifiées (signalées à l'OPCO)</li>
                <li>• Retards répétés</li>
                <li>• Utilisation du téléphone en cours</li>
                <li>• Consommation d'alcool ou de substances</li>
                <li>• Comportements irrespectueux</li>
                <li>• Plagiat ou fraude aux examens</li>
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-[#0B1F3A] p-6 text-white">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <AlertCircle className="text-[#C8A75A]" size={20} />
              Important : Gestion des absences
            </h3>
            <p className="text-sm text-[#F5F5F5]/90">
              Toute absence doit être signalée <strong>le jour même</strong> par email à pedagogie@institut-bia.fr 
              et justifiée sous 48h (certificat médical, attestation...). Les absences non justifiées sont 
              transmises à l'OPCO et à l'entreprise, et peuvent entraîner des sanctions.
            </p>
          </div>
        </div>
      </section>

      {/* Infos pratiques */}
      <section id="pratique" className="py-16 bg-[#F5F5F5] scroll-mt-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <span className="text-[#C8A75A] uppercase tracking-[0.2em] text-xs font-semibold mb-4 block text-center">
            Pratique
          </span>
          <h2 className="font-serif-heading text-3xl font-semibold text-[#0B1F3A] mb-12 text-center">
            Informations pratiques
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 border border-[#E6E6E6]">
              <MapPin className="text-[#C8A75A] mb-4" size={28} />
              <h3 className="font-semibold text-[#0B1F3A] mb-2">Localisation</h3>
              <p className="text-sm text-[#4A4A4A]">
                Institut BIA<br />
                Saint-Denis (93)<br />
                Île-de-France
              </p>
            </div>

            <div className="bg-white p-6 border border-[#E6E6E6]">
              <Train className="text-[#C8A75A] mb-4" size={28} />
              <h3 className="font-semibold text-[#0B1F3A] mb-2">Accès transport</h3>
              <p className="text-sm text-[#4A4A4A]">
                Métro Ligne 13<br />
                Bus : lignes à proximité<br />
                RER selon destination
              </p>
            </div>

            <div className="bg-white p-6 border border-[#E6E6E6]">
              <Car className="text-[#C8A75A] mb-4" size={28} />
              <h3 className="font-semibold text-[#0B1F3A] mb-2">Parking</h3>
              <p className="text-sm text-[#4A4A4A]">
                Parking public à proximité<br />
                Stationnement vélo disponible
              </p>
            </div>

            <div className="bg-white p-6 border border-[#E6E6E6]">
              <Coffee className="text-[#C8A75A] mb-4" size={28} />
              <h3 className="font-semibold text-[#0B1F3A] mb-2">Restauration</h3>
              <p className="text-sm text-[#4A4A4A]">
                Espace détente équipé<br />
                Micro-ondes et réfrigérateur<br />
                Commerces à proximité
              </p>
            </div>

            <div className="bg-white p-6 border border-[#E6E6E6]">
              <Wifi className="text-[#C8A75A] mb-4" size={28} />
              <h3 className="font-semibold text-[#0B1F3A] mb-2">Équipements</h3>
              <p className="text-sm text-[#4A4A4A]">
                WiFi haut débit gratuit<br />
                Salles équipées vidéo<br />
                Espace de travail collaboratif
              </p>
            </div>

            <div className="bg-white p-6 border border-[#E6E6E6]">
              <Users className="text-[#C8A75A] mb-4" size={28} />
              <h3 className="font-semibold text-[#0B1F3A] mb-2">Accessibilité</h3>
              <p className="text-sm text-[#4A4A4A]">
                Locaux accessibles PMR<br />
                Référent handicap disponible<br />
                Aménagements sur demande
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contacts */}
      <section id="contacts" className="py-16 bg-[#0B1F3A] scroll-mt-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <span className="text-[#C8A75A] uppercase tracking-[0.2em] text-xs font-semibold mb-4 block text-center">
            Contacts
          </span>
          <h2 className="font-serif-heading text-3xl font-semibold text-white mb-12 text-center">
            Vos interlocuteurs
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { role: 'Responsable pédagogique', email: 'pedagogie@institut-bia.fr', desc: 'Questions sur la formation, le programme, les évaluations' },
              { role: 'Administration', email: 'contact@institut-bia.fr', desc: 'Questions administratives, contrats, documents' },
              { role: 'Référent handicap', email: 'handicap@institut-bia.fr', desc: 'Aménagements spécifiques, accompagnement PSH' }
            ].map((contact, i) => (
              <div key={i} className="bg-white/5 backdrop-blur border border-white/10 p-6 text-center">
                <div className="w-12 h-12 bg-[#C8A75A] flex items-center justify-center mx-auto mb-4">
                  <Users className="text-[#0B1F3A]" size={24} />
                </div>
                <h3 className="text-white font-semibold mb-2">{contact.role}</h3>
                <a href={`mailto:${contact.email}`} className="text-[#C8A75A] text-sm hover:underline block mb-3">
                  {contact.email}
                </a>
                <p className="text-[#F5F5F5]/70 text-sm">{contact.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[#4A4A4A] mb-6">
            Vous avez des questions ? N'hésitez pas à nous contacter.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/espace-etudiant">
              <Button variant="outline" className="border-[#0B1F3A] text-[#0B1F3A] rounded-none px-6">
                Retour à l'espace étudiant
              </Button>
            </Link>
            <Link to="/contact">
              <Button className="bg-[#0B1F3A] text-white hover:bg-[#061326] rounded-none px-6">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LivretAccueil;
